import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Alert} from '@material-ui/lab';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  TextField,
  Button,
  Checkbox,
  Link,
  Grid,
} from '@material-ui/core';
import HttpsIcon from '@material-ui/icons/Https';

import {axiosInstance} from 'utils/auth.service';

import PaiementStripe from './PaymentMethods/PaiementStripe';
import PaiementPostFinance from './PaymentMethods/PaiementPostFinance';
import PaiementCinecheque from './PaymentMethods/PaiementCinecheque';
import PaiementBanContact from './PaymentMethods/PaiementBanContact';
import FreeBooking from './FreeBooking';
import CartoucheSeance from 'components/CartoucheSeance';
import cardsLogo from 'assets/logos_payment.svg';
import bancontactLogo from 'assets/logo_bancontact.png';

import './PaiementChoices.scss';

const PAYMENT_METHODS = {
  postfinance: {
    name: 'postfinance',
    component: PaiementPostFinance,
  },
  stripe: {
    name: 'card',
    component: PaiementStripe,
    logo: cardsLogo,
  },
  cinecheque: {
    name: 'cinecheque',
    component: PaiementCinecheque,
  },
  bancontact: {
    name: 'bancontact',
    component: PaiementBanContact,
    logo: bancontactLogo,
  },
};

const getPaymentMethodComponent = (availablePaymentMethods, choice) => {
  if (availablePaymentMethods.length === 1) {
    return availablePaymentMethods[0].component;
  }
  return (PAYMENT_METHODS[choice] || PAYMENT_METHODS.stripe).component;
};

const getReducedPrice = (amount, coupon) => {
  if (!coupon) return amount;
  if (coupon.free || coupon.discount >= amount) {
    return 0;
  }
  return (amount - coupon.discount) / 100;
};

const PaiementChoices = ({
  seance,
  seanceId,
  conf,
  appState,
  onClose,
  onOk,
  amount,
  go,
  email,
  keepOpen,
  onPaymentMethodChoice,
  onErreur,
}) => {
  const [choice, setChoice] = useState(null);
  const [activeStep, setActiveStep] = useState(seance.access_by_promo_code_only ? 0 : 1);

  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [lastname, setLastname] = useState('');
  const [firstname, setFirstname] = useState('');
  const [postalAddress, setPostalAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [cityName, setCityName] = useState('');
  const [country, setCountry] = useState('');

  /* Error handling for customer inputs */
  const [lastNameError, setLastNameError] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [postalAddressError, setPostalAddressError] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [cityNameError, setCityNameError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [error, setError] = useState(null);

  const {t, i18n} = useTranslation();
  const availablePaymentMethods = conf.PAYMENT_MODES.split(',').map(
    paymentMode => PAYMENT_METHODS[paymentMode] || {name: 'not-supported-payment-method'},
  );
  /* Check for swiss payment method */
  const isPostFinance = availablePaymentMethods.some(method => method.name === 'postfinance');
  console.log('isPostFinance', isPostFinance);

  const handlePaymentMethodChoice = paymentMethodName => {
    setChoice(paymentMethodName);
    isPostFinance ? setActiveStep(4) : setActiveStep(3);
    onPaymentMethodChoice(paymentMethodName);
  };

  const handleSubmitCouponCode = e => {
    e.preventDefault();
    if (!couponCode) {
      return;
    }
    setError(null);
    setCoupon(null);
    /* Using new API for testing, if OK update anothers calls */
    axiosInstance
      .get(`/discount_codes/${couponCode}/seances/${seance.seance_id}`, {
        withCredentials: true,
      })
      .then(({data}) => {
        if (!data.validity) {
          setError(data.reason?.code);
          return;
        }

        setCoupon(data.discount_code);
        setActiveStep(activeStep + 1);
      })
      .catch(err => {
        setError('server_error');
      });
  };

  /* Logic for customer fields form */
  const isButtonDisabled = !lastname || !firstname || !postalAddress || !postalCode || !cityName || !country;

  const handleInputChange = (e, setState, setErrorState) => {
    const {name, value, validity} = e.target;
    setState(value);
    setErrorState(!validity.valid);
  };

  const handleLastNameChange = e => {
    handleInputChange(e, setLastname, setLastNameError);
  };

  const handleFirstNameChange = e => {
    handleInputChange(e, setFirstname, setFirstNameError);
  };

  const handlePostalAddressChange = e => {
    handleInputChange(e, setPostalAddress, setPostalAddressError);
  };

  const handlePostalCodeChange = e => {
    handleInputChange(e, setPostalCode, setPostalCodeError);
  };

  const handleCityNameChange = e => {
    handleInputChange(e, setCityName, setCityNameError);
  };

  const handleCountryChange = e => {
    handleInputChange(e, setCountry, setCountryError);
  };

  const handleSubmitCustomerInfo = e => {
    e.preventDefault();
    if (e.target.checkValidity()) {
      setActiveStep(activeStep + 1);
    } else {
      alert(`${t('formulaire-invalide-veuillez-renseigner-tout-les-champs')}`);
    }
  };

  const reducedPrice = getReducedPrice(amount, coupon);

  const renderPaymentMethod = () => {
    if (reducedPrice === 0) {
      return <FreeBooking onOk={onOk} seanceId={seance.seance_id} coupon={coupon} />;
    }
    const PaymentMethodComponent = getPaymentMethodComponent(availablePaymentMethods, choice);

    return (
      <div>
        <PaymentMethodComponent
          onClose={onClose}
          onOk={onOk}
          go={go}
          email={email}
          coupon={coupon}
          locale={i18n.language}
          conf={conf}
          seance={seance}
          seanceId={seanceId}
          appState={appState}
          keepOpen={keepOpen}
          onErreur={onErreur}
          customerInfos={{
            lastname: lastname,
            firstname: firstname,
            postal_address: postalAddress,
            postal_code: postalCode,
            city_name: cityName,
            country: country,
          }}
        />
      </div>
    );
  };

  return (
    <Dialog open onClose={onClose} scroll="body" className="my-dialog stripe1">
      <DialogTitle className="payment-title">{t('acheter-mon-ticket')}</DialogTitle>
      <Typography variant="subtitle1" align="center">
        {email}
      </Typography>
      <DialogContent>
        <CartoucheSeance hideBtns seance={seance} seanceId={seanceId} appState={appState} />
        {!seance.access_by_promo_code_only && coupon && (
          <>
            <p className="prix striked">
              {parseFloat(amount / 100).toLocaleString('fr-FR', {minimumFractionDigits: 2})}
              {conf.CURRENCY_SYMBOL}
            </p>
            <p className="prix">
              {parseFloat(reducedPrice).toLocaleString('fr-FR', {minimumFractionDigits: 2})}
              {conf.CURRENCY_SYMBOL}
            </p>
          </>
        )}
        {!seance.access_by_promo_code_only && !coupon && (
          <p className="prix">
            {parseFloat(amount / 100).toLocaleString('fr-FR', {minimumFractionDigits: 2})}
            {conf.CURRENCY_SYMBOL}
          </p>
        )}
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step key="discountCode">
            <StepLabel style={{cursor: 'pointer'}} onClick={() => setActiveStep(0)}>
              {t('saisir-un-code-de-reduction')}
            </StepLabel>
            <StepContent>
              <form onSubmit={handleSubmitCouponCode} className="couponCodeForm">
                <TextField
                  label={t('code-de-reduction')}
                  name="couponCode"
                  variant="filled"
                  placeholder={t('code-de-reduction')}
                  value={couponCode}
                  autoFocus
                  onChange={e => setCouponCode(e.target.value)}
                />
                <Button type="submit" variant="contained" color="primary" disabled={!couponCode}>
                  {t('valider')}
                </Button>
              </form>
              <div className="spacer" />
              {error && <Alert severity="error">{t(`coupon_error_${error}`)}</Alert>}
            </StepContent>
          </Step>

          {isPostFinance && (
            <Step key="nameAndAddress">
              <StepLabel style={{cursor: isPostFinance ? 'default' : 'pointer'}} onClick={() => setActiveStep(1)}>
                {t('saisir-vos-informations')}
              </StepLabel>
              <StepContent>
                <form onSubmit={handleSubmitCustomerInfo} noValidate>
                  <TextField
                    required
                    fullWidth
                    margin="dense"
                    label={t('nom')}
                    name="lastname"
                    variant="filled"
                    placeholder={t('nom')}
                    autoFocus
                    inputProps={{
                      pattern: '[A-Za-zÀ-ÖØ-öø-ÿ ]+',
                    }}
                    value={lastname}
                    onChange={handleLastNameChange}
                    error={lastNameError}
                    helperText={lastNameError ? `${t('veuillez-renseigner-votre-nom (lettres-et-espaces)')}` : ''}
                  />
                  <TextField
                    required
                    fullWidth
                    margin="dense"
                    label={t('prenom')}
                    name="firstname"
                    variant="filled"
                    placeholder={t('prenom')}
                    autoFocus
                    inputProps={{
                      pattern: '[A-Za-zÀ-ÖØ-öø-ÿ ]+',
                    }}
                    value={firstname}
                    onChange={handleFirstNameChange}
                    error={firstNameError}
                    helperText={firstNameError ? `${t('veuillez-renseigner-votre-prenom (lettres-et-espaces)')}` : ''}
                  />
                  <TextField
                    required
                    fullWidth
                    margin="dense"
                    label={t('adresse-postale')}
                    name="address"
                    variant="filled"
                    placeholder={t('adresse-postale')}
                    autoFocus
                    inputProps={{
                      pattern: '^[a-zA-Z0-9_, ]+$',
                    }}
                    value={postalAddress}
                    onChange={handlePostalAddressChange}
                    error={postalAddressError}
                    helperText={postalAddressError ? `${t('veuillez-renseigner-votre-adresse-postale')}` : ''}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <TextField
                        required
                        fullWidth
                        margin="dense"
                        label={t('code-postal')}
                        name="postal code"
                        variant="filled"
                        placeholder={t('code-postal')}
                        autoFocus
                        inputProps={{
                          pattern: '^[a-zA-Z0-9_, ]+$',
                        }}
                        value={postalCode}
                        onChange={handlePostalCodeChange}
                        error={postalCodeError}
                        helperText={
                          postalCodeError
                            ? `${t('veuillez-renseigner-votre-code-postal (chiffres-et-lettres-autorisés)')}`
                            : ''
                        }
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        required
                        fullWidth
                        margin="dense"
                        label={t('ville')}
                        name="ville"
                        variant="filled"
                        placeholder={t('ville')}
                        autoFocus
                        inputProps={{
                          pattern: '[A-Za-z ]+',
                        }}
                        value={cityName}
                        onChange={handleCityNameChange}
                        error={cityNameError}
                        helperText={cityNameError ? `${t('veuillez-renseigner-votre-ville(lettres)')}` : ''}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    required
                    fullWidth
                    margin="dense"
                    label={t('pays')}
                    name="country"
                    variant="filled"
                    placeholder={t('pays')}
                    autoFocus
                    inputProps={{
                      pattern: '[A-Za-z ]+',
                    }}
                    value={country}
                    onChange={handleCountryChange}
                    error={countryError}
                    helperText={countryError ? `${t('veuillez-renseigner-votre-pays(lettres)')}` : ''}
                  />
                  <div className="spacer" />
                  <Button type="submit" variant="contained" color="primary" disabled={isButtonDisabled}>
                    {t('valider')}
                  </Button>
                </form>
              </StepContent>
              <div className="spacer" />
            </Step>
          )}

          {/* CGV validation */}
          <Step key="cgv">
            <StepLabel style={{cursor: isPostFinance ? 'default' : 'pointer'}}>
              {t('conditions-générales-de-vente')}
            </StepLabel>
            <StepContent className="cgvForm">
              <Checkbox
                color="primary"
                required
                onClick={() => (isPostFinance ? setActiveStep(3) : setActiveStep(2))}
              />
              <Typography variant="body2" color="textPrimary">
                <Link href="/cgv" target="_blank" rel="noopener" class="cgv-link">
                  {t(`j'accepte-les-conditions-generales-de-vente-et-la-politique-de-confidentialite`)}
                </Link>
              </Typography>
            </StepContent>
          </Step>

          {!seance.access_by_promo_code_only && availablePaymentMethods.length > 1 && reducedPrice !== 0 && (
            <Step key="paymentProcessor">
              <StepLabel
                style={{cursor: isPostFinance ? 'default' : 'pointer'}}
                onClick={isPostFinance ? null : () => setActiveStep(2)}
              >
                {t('choix-du-mode-de-paiement')}
              </StepLabel>
              <StepContent>
                <Paper square elevation={1}>
                  <List component="ul" aria-label="payment choices">
                    {availablePaymentMethods.map(paymentMethod => (
                      <ListItem
                        key={paymentMethod.name}
                        button
                        onClick={() => handlePaymentMethodChoice(paymentMethod.name)}
                      >
                        <ListItemText>
                          {t(paymentMethod.name)}
                          {paymentMethod.logo && (
                            <img
                              className="payment-method-logo"
                              alt={`${paymentMethod.name} logo`}
                              src={paymentMethod.logo}
                            />
                          )}
                        </ListItemText>
                        <ListItemIcon>
                          <HttpsIcon fontSize="small" />
                        </ListItemIcon>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </StepContent>
            </Step>
          )}
          <Step key="paymentInformations">
            <StepLabel>
              {reducedPrice === 0 || seance.access_by_promo_code_only
                ? t('valider-votre-reservation')
                : t('saisie-des-informations')}
            </StepLabel>
            <StepContent>{renderPaymentMethod()}</StepContent>
          </Step>
        </Stepper>
        <div className="spacer" />
      </DialogContent>
    </Dialog>
  );
};

export default PaiementChoices;
